import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { ELASTOMERS, SELECTION_FACTORS } from "@/data/elastomers";
import { useSEO } from "@/lib/seo";

export default function Elastomers() {
  useSEO({
    title: "Elastomers — Compound Selection & Development",
    description:
      "Fourteen elastomer families compounded in-house at Vikas Rolls — from natural rubber and EPDM to polyurethane, silicone and Viton/FKM — selected against your operating conditions.",
  });

  return (
    <>
      <PageHero
        chapter="E"
        label="Material Science"
        title="The Right Elastomer Starts With Your Process"
        sub="Quality rollers begin at the source — raw materials. Our compounding plant formulates each cover using strict quality control and precise technical and chemical knowledge of every elastomer."
        crumbs={[{ label: "Elastomers" }]}
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <SectionHeading chapter="01" label="Selection Factors" title="Nine conditions drive every selection." className="mb-12" />
          <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-px bg-line border border-line mb-8">
            {SELECTION_FACTORS.map((f, i) => (
              <StaggerItem key={f} className="bg-ink-2">
                <div className="p-4 md:p-5 h-full text-center hover:bg-ink-3 transition-colors duration-300">
                  <p className="font-mono-tech text-[10px] text-amber-brand mb-2">{String(i + 1).padStart(2, "0")}</p>
                  <p className="text-xs md:text-sm text-steel-light leading-snug">{f}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <Reveal>
              <p className="max-w-3xl text-steel text-base md:text-lg leading-relaxed">
                A combination of strict quality control, current mixing technology and detailed chemical
                knowledge of each elastomer goes into formulating compounds that operate at peak
                efficiency. Every roller is tested against exacting standards — the result is reduced
                downtime, lower maintenance costs and better process efficiency.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="corner-marks border border-line overflow-hidden">
                <img
                  src="/images/elastomer_compounds.jpg"
                  alt="Colourful elastomer compound spheres representing the range of rubber compounds formulated at Vikas Rolls"
                  loading="lazy"
                  className="img-industrial w-full object-cover"
                  data-testid="elastomer-compounds-image"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper text-paper-ink border-y border-line py-16 md:py-24 bg-blueprint-light">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <SectionHeading dark={false} chapter="02" label="Elastomer Range" title="Fourteen families, compared." className="mb-6" />
          <Reveal>
            <p className="max-w-3xl text-paper-ink/70 text-base leading-relaxed mb-12">
              Typical strengths are shown below to guide first selection. Exact hardness ranges,
              temperature limits and chemical compatibility are confirmed per compound and
              application with our technical team.
            </p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-paper-line border border-paper-line">
            {ELASTOMERS.map((e) => (
              <StaggerItem key={e.name} className="bg-paper">
                <div className="p-6 h-full bg-paper hover:bg-paper-2 transition-colors duration-300" data-testid={`elastomer-card-${e.code.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-bold text-lg leading-tight">{e.name}</h3>
                    <span className="font-mono-tech text-[10px] bg-paper-ink text-paper px-2 py-1 flex-none ml-2">{e.code}</span>
                  </div>
                  <ul className="space-y-2">
                    {e.strengths.map((s) => (
                      <li key={s} className="flex items-start gap-2.5 text-sm text-paper-ink/80">
                        <span className="w-1.5 h-1.5 bg-amber-deep rotate-45 flex-none mt-1.5" aria-hidden="true" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <div className="border border-line bg-ink-2 p-8 md:p-12 grid md:grid-cols-[auto_1fr_auto] gap-8 items-center">
              <FlaskConical size={44} className="text-amber-brand hidden md:block" aria-hidden="true" />
              <div>
                <h2 className="font-display font-black tracking-tight text-2xl md:text-4xl text-steel-light">Elastomer Selection Support</h2>
                <p className="text-steel mt-3 max-w-2xl">
                  Not sure which compound suits your line? Share your operating conditions and process
                  medium — our team will recommend and formulate the right cover.
                </p>
              </div>
              <Link to="/request-a-quote" data-testid="elastomer-support-cta" className="group flex-none inline-flex items-center gap-3 bg-amber-brand text-ink font-display font-bold px-8 py-4 hover:bg-steel-light">
                Get Selection Support
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
